/**
 * Client-side playlist data layer.
 *
 * Architecture:
 *   1. Backend tells us which playlists ({id, name, m3u_url, epg_url}) are
 *      assigned to this activation key + device.
 *   2. We fetch the M3U *directly from the provider* — via the Electron main
 *      process (no CORS, no devtools network entry pointing at the provider).
 *   3. We parse the M3U + EPG in-renderer and cache the parsed objects in
 *      IndexedDB so subsequent app launches are instant.
 *
 * Cache TTL is intentionally long (24h) because we expose a manual
 * "Reload playlist" button — the user controls staleness.
 */
import { parseM3u, parseM3uStream, detectContentType, extractSeriesMeta, extractYear } from "./m3uParser";
import { parseXmlTv } from "./epgParser";
import { isElectron } from "./deviceId";
import { webFetch } from "./webFetch";
import {
  parseXtreamCreds,
  fetchXtreamCatalog,
  enrichChannelsWithXtream,
  harvestXtreamExtras,
} from "./xtreamApi";
import { parseB2cCreds, fetchB2cCatalog, B2C_SCHEMA_VERSION } from "./oneStreamApi";
import { beginLoad, reportLoadProgress, endLoad } from "./loadProgress";
import { debugLog } from "./debugMode";
import { APP_VERSION } from "./version";
// v1.3.82 — SQLite kv shadow-cache used by the tail-end helpers
// (series_eps / vod_cats / vod_streams / live_cats / live_streams /
// short_epg). Static import is safe because playlistCacheSql.js is
// a leaf module (only depends on @capacitor/core + the plugin).
import {
  sqlIsAvailable as _sqlAvail,
  sqlKvGet as _sqlKvGet,
  sqlKvPut as _sqlKvPut,
} from "./playlistCacheSql";
import { triggerCoverHarvest, fetchMetadataSnapshot, FETCH_METADATA_DISABLED, fetchPlaylistSnapshot, SNAPSHOT_NOT_FOUND, SNAPSHOT_FORBIDDEN, SNAPSHOT_NETWORK_ERROR } from "./playerApi";
import {
  getCoverRewriteStats,
  resetCoverRewriteStats,
  rewritePlexThumbUrl,
} from "./imageCache";
// v1.3.76 — in-memory LRU shim over IDB. The first call to any
// `getCachedXxx` still hits IDB (~10-30 ms), every subsequent call
// during the same session returns in < 1 ms. Pairs with the v1.3.75
// cold-start TTL skip to make Browse/LiveTV navigation feel native.
import {
  lruGet,
  lruSet,
  lruInvalidate,
  lruClearAll as _lruClearAll,
} from "./playlistCacheLru";

// v1.4.20 — Memory diagnostics for the Smart TV cold-start pipeline.
//
// Chromium WebViews (Android, most TV OSs) expose `performance.memory`
// with `usedJSHeapSize`, `totalJSHeapSize`, `jsHeapSizeLimit`. Firefox
// does not. Safari does not. So this helper is best-effort — when the
// API isn't there, it emits nothing and never throws.
//
// Emits to the in-app Debug Console (Settings → Debug → view logs)
// via `debugLog`. Use with `enableDebug()` or the magic activation
// key `logtest`.
//
// Format:  [mem tag] used=XX MB / total=YY MB / limit=ZZ MB (utilised NN %)
function logMemory(tag) {
  try {
    if (typeof performance === "undefined" || !performance.memory) return;
    const m = performance.memory;
    const mb = (n) => Math.round(n / 1048576);
    const used = mb(m.usedJSHeapSize);
    const total = mb(m.totalJSHeapSize);
    const limit = mb(m.jsHeapSizeLimit);
    const pct = limit ? Math.round((m.usedJSHeapSize / m.jsHeapSizeLimit) * 100) : 0;
    debugLog(`[mem ${tag}] used=${used} MB / total=${total} MB / limit=${limit} MB (utilised ${pct} %)`);
  } catch {
    /* ignore — diagnostic must never crash the load */
  }
}

const DB_NAME = "za_player_pro";
const STORE = "playlists";         // legacy single-blob store (dropped at v2)
const META_STORE = "playlist_meta"; // v2: per-playlist xtream/etag/etc
const CHAN_STORE = "channels";      // v2: per-channel rows, keyed [pl,ch]
const EPG_STORE = "epg";            // v2: per-tvg_id programme arrays
const SERIES_EP_STORE = "series_episodes"; // v4: per-series episodes,
                                            // keyed `${pl_id}:${series_id}`
const VOD_CATS_STORE = "vod_categories"; // v5: Xtream movie cats, keyed `${pl_id}`
const VOD_STREAMS_STORE = "vod_streams"; // v5: per-category movie lists,
                                          // keyed `${pl_id}:${category_id}`
const LIVE_CATS_STORE = "live_categories"; // v6: Xtream live-tv cats, keyed `${pl_id}`
const LIVE_STREAMS_STORE = "live_streams"; // v6: per-category live channels,
                                            // keyed `${pl_id}:${category_id}`
const SHORT_EPG_STORE = "short_epg"; // v6: per-channel now/next EPG,
                                      // keyed `${pl_id}:${stream_id}`
const DB_VERSION = 6;
const TTL_MS = 24 * 60 * 60 * 1000; // 24h soft TTL
// Series-episodes cache TTL — kept short (30 min) because new
// episodes get released daily and the whole point of moving to
// `get_series_info` is freshness. The fetch is cheap (single small
// JSON per series, only on tile open).
const SERIES_EP_TTL_MS = 30 * 60 * 1000;
// VOD cache TTLs — categories rarely change (24h), movie lists per
// category refresh more often (1h) so newly added movies appear
// without a full playlist reload.
const VOD_CATS_TTL_MS = 24 * 60 * 60 * 1000;
const VOD_STREAMS_TTL_MS = 60 * 60 * 1000;
// Live TV cache TTLs — categories static for a day, channel lists
// per category refresh every 15 min (broadcasters often add /
// rename channels on a same-day cadence), short EPG cached 5 min
// so now/next stays fresh without spamming `get_short_epg`.
const LIVE_CATS_TTL_MS = 24 * 60 * 60 * 1000;
const LIVE_STREAMS_TTL_MS = 15 * 60 * 1000;
const SHORT_EPG_TTL_MS = 5 * 60 * 1000;

// Batch size for the chunked IDB write. Tested up to 200k channels on a
// 256 MB TV WebView heap — 2k rows per transaction never triggers the
// structured-clone OOM that the old "one giant blob" approach hit.
const CHAN_WRITE_BATCH = 2000;

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = req.result;
      // v1 → v2: drop the legacy single-blob store (no migration — the
      // next loadPlaylist will repopulate per-record). Then create the
      // three normalised stores.
      if (db.objectStoreNames.contains(STORE) && ev.oldVersion < 2) {
        try { db.deleteObjectStore(STORE); } catch { /* noop */ }
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE); // keyed by `pl:${id}` (manual)
      }
      if (!db.objectStoreNames.contains(CHAN_STORE)) {
        const s = db.createObjectStore(CHAN_STORE, { keyPath: "_k" });
        // Compound index — `IDBKeyRange.bound(["pl:X",""], ["pl:X",..])`
        // lets us collect every channel for one playlist in a single
        // cursor walk without scanning the whole store.
        s.createIndex("playlist", "playlist_key", { unique: false });
        // Per-category compound index — `[playlist_key, content_type,
        // group]`. Powers per-category lazy-load: Browse only fetches
        // the channels for the active category, keeping cold-start
        // memory at ~10 MB instead of ~150 MB on huge playlists.
        s.createIndex("playlist_cat", ["playlist_key", "content_type", "group"], { unique: false });
      } else if (ev.oldVersion < 3) {
        // v2 → v3: add the per-category compound index to an existing
        // CHAN_STORE without dropping data.
        const tx = req.transaction;
        const s = tx.objectStore(CHAN_STORE);
        if (!s.indexNames.contains("playlist_cat")) {
          s.createIndex("playlist_cat", ["playlist_key", "content_type", "group"], { unique: false });
        }
      }
      if (!db.objectStoreNames.contains(EPG_STORE)) {
        db.createObjectStore(EPG_STORE, { keyPath: "_k" })
          .createIndex("playlist", "playlist_key", { unique: false });
      }
      // v3 → v4: per-series episodes store. Keyed by
      // `${playlist_id}:${series_id}`. Stores
      // `{ episodes, info, seasons, fetched_at }` so the next series
      // tile open is instant without a round trip to the provider.
      if (!db.objectStoreNames.contains(SERIES_EP_STORE)) {
        db.createObjectStore(SERIES_EP_STORE); // manual keys
      }
      // v4 → v5: VOD category + per-category streams stores.
      // Phase 2 of the ITVZA-inspired refactor. `vod_categories`
      // holds the small `get_vod_categories` payload (one entry per
      // playlist). `vod_streams` holds the per-category normalised
      // movie list — only populated on demand when the user opens a
      // category in Browse.
      if (!db.objectStoreNames.contains(VOD_CATS_STORE)) {
        db.createObjectStore(VOD_CATS_STORE); // key = `${pl_id}`
      }
      if (!db.objectStoreNames.contains(VOD_STREAMS_STORE)) {
        db.createObjectStore(VOD_STREAMS_STORE); // key = `${pl_id}:${cat_id}`
      }
      // v5 → v6: Live TV equivalents + short-EPG cache. Phase 3
      // of the refactor — same per-category lazy pattern.
      if (!db.objectStoreNames.contains(LIVE_CATS_STORE)) {
        db.createObjectStore(LIVE_CATS_STORE); // key = `${pl_id}`
      }
      if (!db.objectStoreNames.contains(LIVE_STREAMS_STORE)) {
        db.createObjectStore(LIVE_STREAMS_STORE); // key = `${pl_id}:${cat_id}`
      }
      if (!db.objectStoreNames.contains(SHORT_EPG_STORE)) {
        db.createObjectStore(SHORT_EPG_STORE); // key = `${pl_id}:${stream_id}`
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

/** Read the metadata blob for one playlist (no channels). */
async function idbGetMeta(plKey) {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(META_STORE, "readonly");
    const req = tx.objectStore(META_STORE).get(plKey);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
}

// ============================================================
// Series-episodes cache (v1.3.66)
// ============================================================
//
// Per-series episodes are fetched on-demand from the Xtream
// `get_series_info` JSON endpoint when a user opens a series tile.
// The result is normalised by `fetchXtreamSeriesEpisodes` then
// persisted here so re-opening the same series during the session
// (or on the next app launch) is instant.
//
// Storage key:   `${playlist_id}:${series_id}`
// Value shape:   { episodes: [...], info, seasons, fetched_at }
// TTL:           30 minutes (`SERIES_EP_TTL_MS`)
function _seriesEpKey(playlistId, seriesId) {
  return `${playlistId}:${seriesId}`;
}

// v1.3.82 — SQLite kv fallback + shadow-write helpers. Fold the
// same 4 lines into each of the 6 kv-store pairs below without
// duplicating the try/catch / TTL logic.
async function _kvFallback(scope, key, ttlMs) {
  if (!_sqlAvail()) return null;
  try {
    const row = await _sqlKvGet(scope, key);
    if (!row) return null;
    if (ttlMs > 0 && Date.now() - (row.fetched_at || 0) > ttlMs) return null;
    return row.data;
  } catch { return null; }
}
function _kvShadow(scope, key, data) {
  if (!_sqlAvail()) return;
  _sqlKvPut(scope, key, data).catch(() => {});
}

/**
 * Read cached episodes for one series. Returns `null` if no cache
 * entry exists OR if the entry is older than `SERIES_EP_TTL_MS`.
 * The TTL check happens here (not on write) so an in-flight refresh
 * doesn't briefly miss a fresh entry.
 */
export async function getCachedSeriesEpisodes(playlistId, seriesId) {
  if (!playlistId || !seriesId) return null;
  // v1.3.76 LRU fast-path: returns < 1 ms on a hit.
  const hit = lruGet("series_eps", playlistId, seriesId);
  if (hit !== undefined) return hit;
  try {
    const db = await openDb();
    const key = _seriesEpKey(playlistId, seriesId);
    const value = await new Promise((resolve) => {
      const tx = db.transaction(SERIES_EP_STORE, "readonly");
      const req = tx.objectStore(SERIES_EP_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!value) {
      // v1.3.82 — try the SQL kv shadow before giving up. Populates
      // the LRU on hit so the next call short-circuits at the top.
      const sqlHit = await _kvFallback("series_eps", _seriesEpKey(playlistId, seriesId), SERIES_EP_TTL_MS);
      if (sqlHit) {
        lruSet("series_eps", [playlistId, seriesId], sqlHit);
        return sqlHit;
      }
      return null;
    }
    if (!value.fetched_at || Date.now() - value.fetched_at > SERIES_EP_TTL_MS) {
      return null;
    }
    lruSet("series_eps", [playlistId, seriesId], value);
    return value;
  } catch {
    return null;
  }
}

/** Persist a fresh series-episodes payload to IDB. Best-effort — a
 *  failed write just means the next open will refetch. */
export async function cacheSeriesEpisodes(playlistId, seriesId, payload) {
  if (!playlistId || !seriesId || !payload) return;
  // Invalidate the LRU first so a read between now and the IDB write
  // doesn't serve the stale entry.
  lruInvalidate("series_eps", playlistId, seriesId);
  try {
    const db = await openDb();
    const key = _seriesEpKey(playlistId, seriesId);
    const stored = { ...payload, fetched_at: Date.now() };
    await new Promise((resolve) => {
      const tx = db.transaction(SERIES_EP_STORE, "readwrite");
      const req = tx.objectStore(SERIES_EP_STORE).put(stored, key);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    lruSet("series_eps", [playlistId, seriesId], stored);
    // v1.3.82 — shadow-write to SQL.
    _kvShadow("series_eps", _seriesEpKey(playlistId, seriesId), stored);
  } catch { /* ignore IDB write failures */ }
}

// ============================================================
// VOD cache helpers (v1.3.66 — Phase 2)
// ============================================================
function _vodStreamsKey(playlistId, categoryId) {
  return `${playlistId}:${categoryId == null ? "" : categoryId}`;
}

/** Read cached movie categories for a playlist. Returns `null`
 *  outside the TTL window (24h). */
export async function getCachedVodCategories(playlistId) {
  if (!playlistId) return null;
  const hit = lruGet("vod_cats", playlistId);
  if (hit !== undefined) return hit;
  try {
    const db = await openDb();
    const value = await new Promise((resolve) => {
      const tx = db.transaction(VOD_CATS_STORE, "readonly");
      const req = tx.objectStore(VOD_CATS_STORE).get(String(playlistId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!value) {
      // v1.3.82 — SQL kv fallback for cold IDB (fresh install /
      // storage cleared) — SQL survives IndexedDB wipes on some
      // Smart-TV WebViews that eagerly purge origins.
      const sqlHit = await _kvFallback("vod_cats", String(playlistId), VOD_CATS_TTL_MS);
      if (Array.isArray(sqlHit?.categories)) {
        lruSet("vod_cats", [playlistId], sqlHit.categories);
        return sqlHit.categories;
      }
      return null;
    }
    if (!value.fetched_at || Date.now() - value.fetched_at > VOD_CATS_TTL_MS) {
      return null;
    }
    const cats = Array.isArray(value.categories) ? value.categories : null;
    if (cats) lruSet("vod_cats", [playlistId], cats);
    return cats;
  } catch {
    return null;
  }
}

/** Persist movie categories for a playlist. */
export async function cacheVodCategories(playlistId, categories) {
  if (!playlistId || !Array.isArray(categories)) return;
  lruInvalidate("vod_cats", playlistId);
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(VOD_CATS_STORE, "readwrite");
      const req = tx.objectStore(VOD_CATS_STORE).put(
        { categories, fetched_at: Date.now() },
        String(playlistId),
      );
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    lruSet("vod_cats", [playlistId], categories);
    _kvShadow("vod_cats", String(playlistId), { categories, fetched_at: Date.now() });
  } catch { /* ignore */ }
}

/** Read cached movies for one category (or for the whole playlist
 *  when `categoryId` is null/empty). Returns `null` outside the TTL
 *  window (1h). */
export async function getCachedVodStreams(playlistId, categoryId) {
  if (!playlistId) return null;
  const hit = lruGet("vod_streams", playlistId, categoryId);
  if (hit !== undefined) return hit;
  try {
    const db = await openDb();
    const key = _vodStreamsKey(playlistId, categoryId);
    const value = await new Promise((resolve) => {
      const tx = db.transaction(VOD_STREAMS_STORE, "readonly");
      const req = tx.objectStore(VOD_STREAMS_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!value) {
      const sqlHit = await _kvFallback("vod_streams", key, VOD_STREAMS_TTL_MS);
      if (Array.isArray(sqlHit?.streams)) {
        lruSet("vod_streams", [playlistId, categoryId], sqlHit.streams);
        return sqlHit.streams;
      }
      return null;
    }
    if (!value.fetched_at || Date.now() - value.fetched_at > VOD_STREAMS_TTL_MS) {
      return null;
    }
    const streams = Array.isArray(value.streams) ? value.streams : null;
    if (streams) lruSet("vod_streams", [playlistId, categoryId], streams);
    return streams;
  } catch {
    return null;
  }
}

/** Persist a per-category movie list. */
export async function cacheVodStreams(playlistId, categoryId, streams) {
  if (!playlistId || !Array.isArray(streams)) return;
  lruInvalidate("vod_streams", playlistId, categoryId);
  try {
    const db = await openDb();
    const key = _vodStreamsKey(playlistId, categoryId);
    await new Promise((resolve) => {
      const tx = db.transaction(VOD_STREAMS_STORE, "readwrite");
      const req = tx.objectStore(VOD_STREAMS_STORE).put(
        { streams, fetched_at: Date.now() },
        key,
      );
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    lruSet("vod_streams", [playlistId, categoryId], streams);
    _kvShadow("vod_streams", _vodStreamsKey(playlistId, categoryId), { streams, fetched_at: Date.now() });
  } catch { /* ignore */ }
}

// ============================================================
// Live TV cache helpers (v1.3.66 — Phase 3)
// ============================================================
function _liveStreamsKey(playlistId, categoryId) {
  return `${playlistId}:${categoryId == null ? "" : categoryId}`;
}
function _shortEpgKey(playlistId, streamId) {
  return `${playlistId}:${streamId}`;
}

/** Read cached Live TV categories. Returns null outside the 24h TTL. */
export async function getCachedLiveCategories(playlistId) {
  if (!playlistId) return null;
  const hit = lruGet("live_cats", playlistId);
  if (hit !== undefined) return hit;
  try {
    const db = await openDb();
    const value = await new Promise((resolve) => {
      const tx = db.transaction(LIVE_CATS_STORE, "readonly");
      const req = tx.objectStore(LIVE_CATS_STORE).get(String(playlistId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!value) {
      const sqlHit = await _kvFallback("live_cats", String(playlistId), LIVE_CATS_TTL_MS);
      if (Array.isArray(sqlHit?.categories)) {
        lruSet("live_cats", [playlistId], sqlHit.categories);
        return sqlHit.categories;
      }
      return null;
    }
    if (!value.fetched_at || Date.now() - value.fetched_at > LIVE_CATS_TTL_MS) {
      return null;
    }
    const cats = Array.isArray(value.categories) ? value.categories : null;
    if (cats) lruSet("live_cats", [playlistId], cats);
    return cats;
  } catch {
    return null;
  }
}

/** Persist Live TV categories for a playlist. */
export async function cacheLiveCategories(playlistId, categories) {
  if (!playlistId || !Array.isArray(categories)) return;
  lruInvalidate("live_cats", playlistId);
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(LIVE_CATS_STORE, "readwrite");
      const req = tx.objectStore(LIVE_CATS_STORE).put(
        { categories, fetched_at: Date.now() },
        String(playlistId),
      );
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    lruSet("live_cats", [playlistId], categories);
    _kvShadow("live_cats", String(playlistId), { categories, fetched_at: Date.now() });
  } catch { /* ignore */ }
}

/** Read cached Live TV channels for one category (15-min TTL). */
export async function getCachedLiveStreams(playlistId, categoryId) {
  if (!playlistId) return null;
  const hit = lruGet("live_streams", playlistId, categoryId);
  if (hit !== undefined) return hit;
  try {
    const db = await openDb();
    const key = _liveStreamsKey(playlistId, categoryId);
    const value = await new Promise((resolve) => {
      const tx = db.transaction(LIVE_STREAMS_STORE, "readonly");
      const req = tx.objectStore(LIVE_STREAMS_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!value) {
      const sqlHit = await _kvFallback("live_streams", key, LIVE_STREAMS_TTL_MS);
      if (Array.isArray(sqlHit?.streams)) {
        lruSet("live_streams", [playlistId, categoryId], sqlHit.streams);
        return sqlHit.streams;
      }
      return null;
    }
    if (!value.fetched_at || Date.now() - value.fetched_at > LIVE_STREAMS_TTL_MS) {
      return null;
    }
    const streams = Array.isArray(value.streams) ? value.streams : null;
    if (streams) lruSet("live_streams", [playlistId, categoryId], streams);
    return streams;
  } catch {
    return null;
  }
}

/** Persist a per-category Live TV channel list. */
export async function cacheLiveStreams(playlistId, categoryId, streams) {
  if (!playlistId || !Array.isArray(streams)) return;
  lruInvalidate("live_streams", playlistId, categoryId);
  try {
    const db = await openDb();
    const key = _liveStreamsKey(playlistId, categoryId);
    await new Promise((resolve) => {
      const tx = db.transaction(LIVE_STREAMS_STORE, "readwrite");
      const req = tx.objectStore(LIVE_STREAMS_STORE).put(
        { streams, fetched_at: Date.now() },
        key,
      );
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    lruSet("live_streams", [playlistId, categoryId], streams);
    _kvShadow("live_streams", _liveStreamsKey(playlistId, categoryId), { streams, fetched_at: Date.now() });
  } catch { /* ignore */ }
}

/** Read cached short EPG for one channel (5-min TTL). */
export async function getCachedShortEpg(playlistId, streamId) {
  if (!playlistId || !streamId) return null;
  const hit = lruGet("short_epg", playlistId, streamId);
  if (hit !== undefined) return hit;
  try {
    const db = await openDb();
    const key = _shortEpgKey(playlistId, streamId);
    const value = await new Promise((resolve) => {
      const tx = db.transaction(SHORT_EPG_STORE, "readonly");
      const req = tx.objectStore(SHORT_EPG_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!value) {
      const sqlHit = await _kvFallback("short_epg", _shortEpgKey(playlistId, streamId), SHORT_EPG_TTL_MS);
      if (Array.isArray(sqlHit?.epg)) {
        lruSet("short_epg", [playlistId, streamId], sqlHit.epg);
        return sqlHit.epg;
      }
      return null;
    }
    if (!value.fetched_at || Date.now() - value.fetched_at > SHORT_EPG_TTL_MS) {
      return null;
    }
    const epg = Array.isArray(value.epg) ? value.epg : null;
    if (epg) lruSet("short_epg", [playlistId, streamId], epg);
    return epg;
  } catch {
    return null;
  }
}

/** Persist short EPG snapshot for one channel. */
export async function cacheShortEpg(playlistId, streamId, epg) {
  if (!playlistId || !streamId || !Array.isArray(epg)) return;
  lruInvalidate("short_epg", playlistId, streamId);
  try {
    const db = await openDb();
    const key = _shortEpgKey(playlistId, streamId);
    await new Promise((resolve) => {
      const tx = db.transaction(SHORT_EPG_STORE, "readwrite");
      const req = tx.objectStore(SHORT_EPG_STORE).put(
        { epg, fetched_at: Date.now() },
        key,
      );
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    lruSet("short_epg", [playlistId, streamId], epg);
    _kvShadow("short_epg", _shortEpgKey(playlistId, streamId), { epg, fetched_at: Date.now() });
  } catch { /* ignore */ }
}

/** Collect every channel row belonging to one playlist via the index. */
async function idbGetChannels(plKey) {
  const db = await openDb();
  return new Promise((resolve) => {
    const out = [];
    const tx = db.transaction(CHAN_STORE, "readonly");
    const idx = tx.objectStore(CHAN_STORE).index("playlist");
    const req = idx.openCursor(IDBKeyRange.only(plKey));
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return resolve(out);
      // Strip the internal index fields before handing back to the UI.
      const { _k: _kIgnored, playlist_key: _plIgnored, ...ch } = cur.value;
      out.push(ch);
      cur.continue();
    };
    req.onerror = () => resolve(out);
  });
}

/**
 * Per-category lazy loader. Returns just the channels for one
 * (content_type, group) pair using the `playlist_cat` compound index.
 * Used by Browse.jsx so we never have to hold the full catalog in
 * memory once it's been persisted — a 200k-channel playlist's
 * "Action Movies" category typically pulls in ~500 rows / ~1 MB.
 */
export async function getChannelsByGroup(playlistId, contentType, group) {
  // v1.3.76 LRU shim — repeat tab switches hit JS heap (< 1 ms).
  const hit = lruGet("chan_by_group", playlistId, contentType, group);
  if (hit !== undefined) return hit;
  const plKey = `pl:${playlistId}`;
  const db = await openDb();
  const out = await new Promise((resolve) => {
    const collected = [];
    const tx = db.transaction(CHAN_STORE, "readonly");
    const idx = tx.objectStore(CHAN_STORE).index("playlist_cat");
    const req = idx.openCursor(IDBKeyRange.only([plKey, contentType, group || ""]));
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return resolve(collected);
      const { _k: _kI, playlist_key: _plI, ...ch } = cur.value;
      collected.push(ch);
      cur.continue();
    };
    req.onerror = () => resolve(collected);
  });
  if (out && out.length) lruSet("chan_by_group", [playlistId, contentType, group], out);
  return out;
}

/**
 * Build the category list for one (content_type) directly from IDB —
 * counts unique `group` values without materialising all channels.
 * Result: [{group: "Action", count: 412}, ...].
 */
export async function getCategoriesForType(playlistId, contentType) {
  const hit = lruGet("cats_for_type", playlistId, contentType);
  if (hit !== undefined) return hit;
  const plKey = `pl:${playlistId}`;
  const db = await openDb();
  const list = await new Promise((resolve) => {
    const counts = new Map();
    const tx = db.transaction(CHAN_STORE, "readonly");
    const idx = tx.objectStore(CHAN_STORE).index("playlist_cat");
    // Cursor over the entire [plKey, contentType, *] range. Stepping
    // by primary key lets us count fast without loading row payloads.
    const lo = [plKey, contentType, ""];
    const hi = [plKey, contentType, "\uffff"];
    const req = idx.openCursor(IDBKeyRange.bound(lo, hi));
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) {
        const arr = Array.from(counts.entries()).map(([group, count]) => ({ group, count }));
        arr.sort((a, b) => a.group.localeCompare(b.group));
        return resolve(arr);
      }
      const g = cur.value.group || "";
      counts.set(g, (counts.get(g) || 0) + 1);
      cur.continue();
    };
    req.onerror = () => resolve([]);
  });
  if (list && list.length) lruSet("cats_for_type", [playlistId, contentType], list);
  return list;
}

/** Collect every EPG entry for one playlist (programmes-by-tvg-id map). */
async function idbGetEpg(plKey) {
  const db = await openDb();
  return new Promise((resolve) => {
    const map = {};
    const tx = db.transaction(EPG_STORE, "readonly");
    const idx = tx.objectStore(EPG_STORE).index("playlist");
    const req = idx.openCursor(IDBKeyRange.only(plKey));
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return resolve(map);
      // EPG row shape: { _k, playlist_key, tvg_id, programmes: [...] }
      map[cur.value.tvg_id] = cur.value.programmes;
      cur.continue();
    };
    req.onerror = () => resolve(map);
  });
}

/** Replace meta + channels + epg for one playlist. Chunked writes keep
 *  any single transaction small enough to dodge the structured-clone
 *  OOM that hit huge-playlist v1.2.11 on 256 MB Smart TV WebViews. */
async function idbPutPlaylist(plKey, meta, channels, epgByChannel) {
  const db = await openDb();

  // 1. Wipe the old rows for this playlist so updates don't accumulate
  //    stale channels.
  //
  // v1.4.24 — CHUNKED WIPE.
  //
  // Previous single-cursor-transaction wipe held ALL deleted rows in
  // the IDB write-ahead-log until the transaction committed. On huge
  // catalogs (400 k+ items → ~200 MB of native memory in the WAL) the
  // Android WebView was OOM-killed BEFORE the tx could commit,
  // matching the "manual update crashes app" report. Chunking into
  // separate 5 000-row transactions caps peak native memory to about
  // 3-5 MB per commit and lets the OS reclaim memory between chunks.
  const WIPE_BATCH = 5000;
  const wipe = async (store) => {
    logMemory(`wipe-${store}-start`);
    let total = 0;
    while (true) {
      const batch = await new Promise((resolve) => {
        const tx = db.transaction(store, "readwrite");
        const idx = tx.objectStore(store).index("playlist");
        const req = idx.openCursor(IDBKeyRange.only(plKey));
        let count = 0;
        req.onsuccess = () => {
          const cur = req.result;
          if (!cur || count >= WIPE_BATCH) return;
          cur.delete();
          count += 1;
          cur.continue();
        };
        tx.oncomplete = () => resolve(count);
        tx.onerror = () => resolve(count);
      });
      total += batch;
      if (batch === 0) break;
      // Yield to the event loop so the WebView can reclaim WAL
      // memory + paint UI between chunks. Keeps the app responsive
      // on slow TV CPUs.
      await new Promise((r) => setTimeout(r, 0));
    }
    debugLog(`[idbPutPlaylist] wiped ${total} rows from ${store}`);
    logMemory(`wipe-${store}-done`);
  };
  await wipe(CHAN_STORE);
  await wipe(EPG_STORE);

  // 2. Meta row (small, single put).
  await new Promise((resolve) => {
    const tx = db.transaction(META_STORE, "readwrite");
    tx.objectStore(META_STORE).put(meta, plKey);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });

  // 3. Channels — batched writes of `CHAN_WRITE_BATCH` rows per
  //    transaction. Yields to the event loop between batches so the
  //    UI thread can paint progress + the click feedback ripple.
  //
  // v1.4.27 — Diagnostic: track failed batches. Prior versions
  // silently `resolve()`d on `tx.onerror`, which meant a QuotaExceeded
  // error on batch N would silently discard channels N…end while
  // showing "playlist loaded" to the user. On 400 k-row Xtream
  // catalogs the IDB quota (typically ~1 GB on Android WebView, less
  // on Samsung/LG TVs) IS reachable, and the symptom matches: user
  // sees fewer live/VOD channels than the provider actually has. Log
  // the first error we see and the total dropped batches so the debug
  // console makes the truncation visible.
  let writtenRows = 0;
  let droppedBatches = 0;
  let firstWriteError = null;
  for (let i = 0; i < channels.length; i += CHAN_WRITE_BATCH) {
    const slice = channels.slice(i, i + CHAN_WRITE_BATCH);
    const batchResult = await new Promise((resolve) => {
      const tx = db.transaction(CHAN_STORE, "readwrite");
      const store = tx.objectStore(CHAN_STORE);
      for (const ch of slice) {
        // The persisted record carries an internal compound key + an
        // index-friendly playlist_key. The UI never sees these — they
        // get stripped on read in idbGetChannels.
        //
        // `group` and `content_type` MUST be strings so the compound
        // [playlist_key, content_type, group] index actually indexes
        // the row — IDB silently drops entries whose key parts are
        // undefined/null, which on Plex-backed channels (no
        // group_title) made the lazy-load come up empty in v1.2.16.
        store.put({
          ...ch,
          group: ch.group || "",
          content_type: ch.content_type || "",
          _k: `${plKey}|${ch.id || ch.stream_url}`,
          playlist_key: plKey,
        });
      }
      tx.oncomplete = () => resolve({ ok: true, count: slice.length });
      tx.onerror = () =>
        resolve({
          ok: false,
          count: slice.length,
          err:
            tx.error?.name +
              (tx.error?.message ? ": " + tx.error.message : "") ||
            "unknown IDB error",
        });
      tx.onabort = () =>
        resolve({
          ok: false,
          count: slice.length,
          err:
            "aborted: " +
              (tx.error?.name || "") +
              (tx.error?.message ? " " + tx.error.message : "") || "aborted",
        });
    });
    if (batchResult.ok) {
      writtenRows += batchResult.count;
    } else {
      droppedBatches += 1;
      if (!firstWriteError) firstWriteError = batchResult.err;
    }
    // Cooperative yield — keeps the WebView responsive on TVs.
    await new Promise((r) => setTimeout(r, 0));
  }
  if (droppedBatches > 0) {
    debugLog(
      `[idbPutPlaylist] ⚠️ ${droppedBatches} CHAN_STORE batch(es) FAILED — wrote ${writtenRows}/${channels.length} rows. First error: ${firstWriteError}. Will fall through to SQLite shadow-write.`,
    );
  } else {
    debugLog(
      `[idbPutPlaylist] CHAN_STORE wrote ${writtenRows}/${channels.length} rows across ${Math.ceil(channels.length / CHAN_WRITE_BATCH)} batches`,
    );
  }

  // 4. EPG — same chunking pattern. Each entry is the full programmes
  //    array for one channel, so we batch by channel count, not rows.
  const tvgIds = Object.keys(epgByChannel || {});
  for (let i = 0; i < tvgIds.length; i += CHAN_WRITE_BATCH) {
    const slice = tvgIds.slice(i, i + CHAN_WRITE_BATCH);
    await new Promise((resolve) => {
      const tx = db.transaction(EPG_STORE, "readwrite");
      const store = tx.objectStore(EPG_STORE);
      for (const tvg of slice) {
        store.put({
          _k: `${plKey}|${tvg}`,
          playlist_key: plKey,
          tvg_id: tvg,
          programmes: epgByChannel[tvg],
        });
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    await new Promise((r) => setTimeout(r, 0));
  }

  // v1.3.82 — Shadow-write the same data to the SQLite backend when
  // it's available (native builds only).
  //
  // v1.4.27 — If any IDB batch above dropped rows, we AWAIT the SQL
  // write and log its outcome, so subsequent per-category reads can
  // fall back to the complete SQLite copy instead of the truncated
  // IDB one. On a healthy write we keep the old fire-and-forget
  // behaviour so the cold-start path stays exactly as fast as before.
  const forceAwaitSql = droppedBatches > 0;
  try {
    // Lazy-imported to avoid a static import cycle between
    // playlistCache.js ↔ cacheBackend.js ↔ playlistCacheSql.js.
    const mod = await import("./cacheBackend");
    if (typeof mod.shadowWritePlaylist === "function") {
      const p = mod.shadowWritePlaylist(
        String(plKey).replace(/^pl:/, ""),
        meta,
        channels,
        epgByChannel,
      );
      if (forceAwaitSql) {
        try {
          const res = await p;
          debugLog(
            `[idbPutPlaylist] SQL recovery write: channels=${res?.channels || 0}, epg=${res?.epg || 0}, meta=${res?.meta || false}`,
          );
        } catch (e) {
          debugLog(
            `[idbPutPlaylist] SQL recovery write FAILED: ${e?.message || e}`,
          );
        }
      } else {
        p.catch(() => {});
      }
    }
  } catch { /* ignore — IDB is still the source of truth */ }
}

/** Delete every row belonging to a single playlist. */
async function idbDeletePlaylist(plKey) {
  const db = await openDb();
  await new Promise((resolve) => {
    const tx = db.transaction(META_STORE, "readwrite");
    tx.objectStore(META_STORE).delete(plKey);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
  const wipe = (store) =>
    new Promise((resolve) => {
      const tx = db.transaction(store, "readwrite");
      const idx = tx.objectStore(store).index("playlist");
      const req = idx.openCursor(IDBKeyRange.only(plKey));
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        cur.delete();
        cur.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  await wipe(CHAN_STORE);
  await wipe(EPG_STORE);
  // v1.3.82 — Also purge the SQLite mirror so a hard reset actually
  // removes the playlist everywhere. Fire-and-forget.
  try {
    const mod = await import("./cacheBackend");
    if (mod?.sqlIsAvailable?.()) {
      const sql = await import("./playlistCacheSql");
      sql.sqlDeletePlaylist(String(plKey).replace(/^pl:/, "")).catch(() => {});
    }
  } catch { /* ignore */ }
}

// In-memory cache for the current session — saves the re-parse cost.
const _memCache = new Map();

// ============================================================
// One-shot migration: re-classify cached channels (v1.2.18).
//
// Channels persisted by v1.2.17 and earlier carry the broken
// content_type from m3uParser's pre-fix `detectContentType`, which
// matched `endsWith(".mkv")` against the raw stream URL — failing
// for any URL that carried a query string (Plex `?X-Plex-Token=`,
// signed CDN links, etc.). Those rows got stored as `live_tv` so
// posters never rendered and TMDB enrichment never fired.
//
// On the very first launch of v1.2.18+ we walk CHAN_STORE once with
// a cursor, re-run the fixed `detectContentType` (and re-extract
// series/year metadata for rows that flip type), and write back only
// the records that actually changed. Flagged in localStorage so we
// don't pay the cursor-walk cost on subsequent launches.
// ============================================================
const MIGRATION_FLAG = "zapp_content_type_migration_v1222";
let _migrationPromise = null;

async function maybeMigrateContentTypes() {
  if (_migrationPromise) return _migrationPromise;
  try {
    if (localStorage.getItem(MIGRATION_FLAG) === "done") {
      _migrationPromise = Promise.resolve({ skipped: true });
      return _migrationPromise;
    }
  } catch {
    // localStorage might be unavailable (private mode etc.) — without
    // a persistent flag we'd re-run forever. Treat as "already done"
    // to avoid the cost; users in that mode can re-import anyway.
    return { skipped: true };
  }

  _migrationPromise = (async () => {
    const db = await openDb();
    let scanned = 0;
    let updated = 0;
    await new Promise((resolve) => {
      const tx = db.transaction(CHAN_STORE, "readwrite");
      const store = tx.objectStore(CHAN_STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        scanned++;
        const row = cur.value;
        const newType = detectContentType(row.group || "", row.name || "", row.stream_url || "");
        if (newType !== (row.content_type || "")) {
          const patched = { ...row, content_type: newType };
          // Re-derive series/year too — pre-fix rows that flipped from
          // live_tv → series/movie never got these populated, which is
          // why TMDB enrichment came up empty even on devices that DID
          // hit the right grid.
          if (newType === "series") {
            Object.assign(patched, extractSeriesMeta(row.name || ""));
          } else if (newType === "movie") {
            patched.year = extractYear(row.name || "");
          }
          cur.update(patched);
          updated++;
        }
        cur.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    try { localStorage.setItem(MIGRATION_FLAG, "done"); } catch { /* noop */ }
    debugLog(`[migration v1.2.18] scanned=${scanned} updated=${updated}`);
    return { scanned, updated };
  })().catch((e) => {
    debugLog(`[migration v1.2.18] FAILED: ${e?.message || e}`);
    // Don't mark done on failure so the next launch retries.
    _migrationPromise = null;
    return { error: e?.message || String(e) };
  });
  return _migrationPromise;
}

// Kick off the migration as soon as this module is imported — runs in
// parallel with the splash, finishes long before the user touches the
// Browse grid. Fire-and-forget; loadPlaylist below awaits it explicitly
// so the first read always sees the post-migration data.
if (typeof window !== "undefined" && typeof indexedDB !== "undefined") {
  maybeMigrateContentTypes();
}

// Headers we add to every catalog/EPG/stream fetch when the admin has
// configured a global User-Agent override (Settings → Player User-Agent).
// `window.__ZAPP_UA__` is populated by App.js's bootstrap on launch.
// Returns an empty object when no override is active so the engine
// default UA is used.
function _zappFetchHeaders() {
  const ua = typeof window !== "undefined" ? window.__ZAPP_UA__ : null;
  return ua ? { "User-Agent": ua } : {};
}

async function rawFetchText(url) {
  // Electron → IPC (CORS-free). The Electron host respects the
  // session-wide UA we set at startup; nothing to do here.
  if (isElectron() && window.electronAPI?.fetchUrl) {
    const r = await window.electronAPI.fetchUrl(url);
    if (!r?.ok) throw new Error(r?.error || `HTTP ${r?.status || "?"}`);
    return r.body;
  }
  // Web fallback — v1.4.8: routes through the backend `/api/proxy/fetch`
  // when running in Web Player mode so CORS-blocked provider APIs and
  // EPG XML feeds actually work in a browser tab. Native / same-origin
  // calls fall through to plain `fetch()` untouched.
  const r = await webFetch(url, { headers: _zappFetchHeaders(), cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);

  // EPG fix: many providers serve gzipped XMLTV (`.xml.gz`) with a
  // ``Content-Type: application/octet-stream`` and NO
  // ``Content-Encoding: gzip`` header, so the browser hands us the
  // RAW gzip bytes. Detect by magic bytes (0x1f 0x8b) or `.gz` URL
  // suffix and decompress via the built-in DecompressionStream. This
  // is the #1 root cause of "EPG doesn't work" complaints.
  const lcUrl = url.toLowerCase();
  const ct = (r.headers.get("Content-Type") || "").toLowerCase();
  const probablyGzip =
    lcUrl.endsWith(".gz") ||
    lcUrl.includes(".gz?") ||
    ct.includes("gzip") ||
    ct.includes("octet-stream") ||
    ct.includes("application/x-gzip");

  if (probablyGzip && typeof DecompressionStream !== "undefined") {
    const buf = await r.arrayBuffer();
    const u8 = new Uint8Array(buf);
    // gzip magic header? if not — maybe the server actually sent
    // plain XML with a misleading Content-Type. Sniff and fall back.
    if (u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b) {
      const ds = new DecompressionStream("gzip");
      const decompressed = await new Response(
        new Blob([buf]).stream().pipeThrough(ds)
      ).text();
      return decompressed;
    }
    // Magic didn't match — treat as plain text.
    return new TextDecoder("utf-8").decode(u8);
  }

  return await r.text();
}

/**
 * Return parsed playlist data: { channels, epgByChannel, fetched_at, source: "cache" | "network" }.
 *
 * @param {object} playlist {id, name, m3u_url, epg_url}
 * @param {object} opts { force?: boolean, autoRefresh?: boolean, loadEpg?: boolean }
 *   autoRefresh — when true, skip the heavy B2C metadata-overlay step
 *   on huge (>200k channel) catalogs to avoid OOM-ing low-spec Smart
 *   TVs during a cold-start auto refresh. The user can still trigger
 *   the full enrichment via the manual "Reload Playlist" button (which
 *   passes autoRefresh=false through reloadPlaylist).
 *   loadEpg — v1.3.71. Defaults to `!autoRefresh` — i.e. EPG is
 *   skipped on cold-start auto-refresh and fetched only when the
 *   user manually reloads OR when LiveTv.jsx explicitly requests it
 *   via `loadEpgOnDemand`. Saves ~600 KB + ~400 ms on cold launch
 *   when the user never visits the Live TV page in the session.
 */
export async function loadPlaylist(playlist, { force = false, autoRefresh = false, loadEpg } = {}) {
  if (loadEpg === undefined) loadEpg = !autoRefresh;
  if (!playlist?.id || !playlist?.m3u_url) {
    return { channels: [], epgByChannel: {}, fetched_at: 0, source: "empty" };
  }
  const key = `pl:${playlist.id}`;

  // 1. memory
  if (!force && _memCache.has(key)) return _memCache.get(key);

  // Ensure the one-shot v1.2.18 content_type migration finished before
  // we serve anything out of IDB — otherwise the first launch could
  // hand the UI the pre-migration (broken) Plex rows.
  await maybeMigrateContentTypes();

  // 2. indexeddb — ALWAYS prefer cache if present. The user's
  // "Reload Playlist" button passes force=true to trigger a fresh
  // fetch; everything else just trusts the cached snapshot so the
  // first paint after launch is instant even on a 590k-channel
  // playlist. The previous 24 h TTL meant some TV users were
  // re-parsing a 100 MB M3U every single day — exactly what the
  // user complained about.
  if (!force) {
    // v2 schema — reassemble from meta + channels + epg stores. The
    // first-paint cost is the channel cursor walk, but it's
    // sub-second for 200k items because IndexedDB serves rows from
    // its on-disk cache without re-cloning each value.
    const meta = await idbGetMeta(key);
    if (meta) {
      // B2C cache schema check — when the adapter's output shape
      // changes (new fields, episode rows added, etc.) we bump
      // B2C_SCHEMA_VERSION and ignore older caches. Forces an
      // automatic re-fetch on the very next load after upgrade so
      // users get the fix without manual "Reload Playlist".
      const isB2c = String(playlist.m3u_url || "").toLowerCase().startsWith("b2c://");
      const cacheVersion = meta.b2c_schema_version || 0;
      if (isB2c && cacheVersion < B2C_SCHEMA_VERSION) {
        debugLog(
          `[loadPlaylist] B2C cache schema ${cacheVersion} < current ${B2C_SCHEMA_VERSION} — invalidating`
        );
        await idbDeletePlaylist(key);
      } else {
        const [channels, epgByChannel] = await Promise.all([
          idbGetChannels(key),
          idbGetEpg(key),
        ]);
        if (channels.length > 0) {
          // Restore the persisted Xtream catalog diagnostic so the
          // Settings card shows the right numbers even on a cache hit
          // (the catalog endpoint isn't re-hit until "Reload Playlist").
          try {
            if (typeof window !== "undefined" && meta.xtream_diag) {
              window.__ZAPP_XTREAM_DIAG__ = meta.xtream_diag;
            }
          } catch {/* noop */}
          const result = {
            ...meta,
            channels,
            epgByChannel,
            source: "cache",
          };
          _memCache.set(key, result);
          return result;
        }
      }
    }
  }

  // 3. fetch + parse
  beginLoad();
  // Redact creds out of the URL before it hits the debug overlay — Xtream
  // URLs carry the user's username/password in plain text query params,
  // and the B2C `b2c://user:pass@host` scheme carries them in userinfo.
  const safeUrl = String(playlist.m3u_url || "")
    .replace(/(username|password|token)=[^&]+/gi, "$1=***")
    .replace(/^(b2c:\/\/)[^@]+@/i, "$1***:***@");
  debugLog(`[loadPlaylist] BEGIN id=${playlist.id} url=${safeUrl} app=v${APP_VERSION}`);
  logMemory("BEGIN");
  // Reset Plex-rewrite counters at the start of every load so the
  // post-load debug line reflects ONLY the current playlist.
  resetCoverRewriteStats();
  // Fire-and-forget: nudge the backend to harvest any new Plex covers
  // for this playlist. Gated to once per 24h per playlist so a quick
  // open / close / open cycle doesn't spam the backend — the manual
  // "Reload Playlist" button always wipes this stamp via reloadPlaylist().
  const HARVEST_STAMP_KEY = `vupp_cover_harvest_at__${playlist.id}`;
  try {
    const last = parseInt(localStorage.getItem(HARVEST_STAMP_KEY) || "0", 10);
    if (Date.now() - last > 24 * 60 * 60 * 1000) {
      localStorage.setItem(HARVEST_STAMP_KEY, String(Date.now()));
      triggerCoverHarvest(playlist.id)
        .then((res) => {
          if (res && res.status) {
            debugLog(`[cover-harvest] ${res.status} playlist=${playlist.id}`);
          } else if (res === null) {
            debugLog(`[cover-harvest] no response (endpoint missing on backend?) playlist=${playlist.id}`);
          }
        })
        .catch((e) => debugLog(`[cover-harvest] failed: ${e?.message || e}`));
    } else {
      const minsAgo = Math.round((Date.now() - last) / 60000);
      debugLog(`[cover-harvest] skipped — last fired ${minsAgo} min ago (24h gate)`);
    }
  } catch (e) {
    debugLog(`[cover-harvest] threw: ${e?.message || e}`);
  }
  try {
    // Quick "Has anything changed?" check via HEAD — many IPTV panels
    // return Last-Modified / ETag headers. When force-reloading we hit
    // HEAD first and skip the entire re-fetch + re-parse if the
    // upstream hasn't moved since our last successful load. Turns a
    // 60 s reload into a 200 ms no-op when the user's provider hasn't
    // changed anything.
    const cached = await idbGetMeta(key);
    if (force && cached && (cached.etag || cached.last_modified)) {
      try {
        // Cache-busted HEAD — see the main fetch below for rationale.
        // v1.4.8 — routed through webFetch so http:// providers work
        // from https:// web-player pages (mixed-content block).
        const headResp = await webFetch(playlist.m3u_url, { method: "HEAD", cache: "no-store" });
        if (headResp.ok) {
          const newEtag = headResp.headers.get("etag");
          const newLm = headResp.headers.get("last-modified");
          const unchanged =
            (newEtag && newEtag === cached.etag) ||
            (newLm && newLm === cached.last_modified);
          if (unchanged) {
            debugLog("[loadPlaylist] upstream unchanged — using cache");
            // Cache hit on the validation path — pull the full per-record
            // channels + EPG from disk just like the cold-start branch.
            let [channels, epgByChannel] = await Promise.all([
              idbGetChannels(key),
              idbGetEpg(key),
            ]);
            // v1.4.27 — Truncation recovery. If the meta row remembers
            // a larger channel count than we just read back from IDB,
            // one or more `idbPutPlaylist` batches must have been
            // silently dropped (typically QuotaExceeded on 400k-row
            // Xtream catalogs — the "Live TV / Movies show fewer
            // channels than expected" bug). Fall back to the SQLite
            // shadow copy which stores rows in a single transaction
            // and is not subject to IndexedDB's per-store quota.
            const expected = Number(cached.channel_count || 0);
            if (expected > 0 && Array.isArray(channels) && channels.length < expected) {
              try {
                const { sqlIsAvailable } = await import("./playlistCacheSql");
                if (sqlIsAvailable && sqlIsAvailable()) {
                  const sql = await import("./playlistCacheSql");
                  const sqlRows = await sql.sqlGetAllChannels(String(key).replace(/^pl:/, ""));
                  if (Array.isArray(sqlRows) && sqlRows.length > channels.length) {
                    debugLog(
                      `[loadPlaylist] IDB truncated (${channels.length}/${expected}) — recovered ${sqlRows.length} rows from SQLite`,
                    );
                    channels = sqlRows;
                  } else {
                    debugLog(
                      `[loadPlaylist] IDB truncated (${channels.length}/${expected}) — SQL also short (${sqlRows?.length || 0})`,
                    );
                  }
                }
              } catch (e) {
                debugLog(`[loadPlaylist] SQL truncation-recovery failed: ${e?.message || e}`);
              }
            }
            try {
              if (typeof window !== "undefined" && cached.xtream_diag) {
                window.__ZAPP_XTREAM_DIAG__ = cached.xtream_diag;
              }
            } catch {/* noop */}
            const result = {
              ...cached,
              channels,
              epgByChannel,
              source: "cache-validated",
            };
            _memCache.set(key, result);
            endLoad();
            return result;
          }
        }
      } catch (e) {
        debugLog(`[loadPlaylist] HEAD check failed (continuing): ${e?.message || e}`);
      }
    }
    // 1-Stream B2C Quick Connect — when the playlist URL uses our
    // Legacy migration: if the stored ``m3u_url`` still uses our
    // ``b2c://user:pass@host`` Quick-Connect scheme (created by an older
    // build of the admin panel), TRANSPARENTLY rewrite it to the
    // equivalent Xtream Codes ``/get.php?…&output=m3u8`` URL and proceed
    // through the regular M3U pipeline below.
    //
    // Why: the architectural rule is "channels, movies, series and EPG
    // come exclusively from the M3U URL — `b2c_url` is metadata-only".
    // We must never load the catalog via the B2C REST API as a primary
    // source; otherwise streams, EPG and channel lists could disagree
    // between devices that have / don't have a B2C overlay configured.
    const b2cCreds = parseB2cCreds(playlist.m3u_url);
    if (b2cCreds) {
      debugLog(
        "[loadPlaylist] m3u_url is b2c:// (legacy) — rewriting to Xtream get.php for primary load"
      );
      const u = encodeURIComponent(b2cCreds.username);
      const p = encodeURIComponent(b2cCreds.password);
      playlist = {
        ...playlist,
        m3u_url: `${b2cCreds.base}/get.php?username=${u}&password=${p}&type=m3u_plus&output=m3u8`,
        epg_url:
          playlist.epg_url ||
          `${b2cCreds.base}/xmltv.php?username=${u}&password=${p}`,
        // Promote the original `b2c://` URL into `b2c_url` so it remains
        // available for the metadata overlay step further down.
        b2c_url: playlist.b2c_url || playlist.m3u_url,
      };
    }

    // Streaming fetch + parse — never holds the full response body in
    // memory. On a 590k-channel IPTV playlist that's the difference
    // between needing ~250 MB peak (text + lines + result) and ~30 MB
    // (current chunk + result). The TV WebView heap is typically capped
    // at 256 MB so the old approach OOM'd before the first progress
    // tick. We only use streaming for direct fetch — the Electron IPC
    // bridge already returns the whole text so we fall back to the
    // text parser there.
    let parsedChannels;
    let embeddedEpg;
    let respEtag = null;
    let respLastModified = null;
    let parseSource = "streaming";

    // v1.3.48 — Try the server-side Pre-parse Snapshot FIRST. If the
    // admin has run /admin/playlists/{id}/run-playlist-snapshot the
    // backend has already done the M3U fetch + parse work, so we can
    // skip ~60-90 s of cold-start cost on huge catalogs and just
    // download the gzipped JSON result.
    //
    // v1.3.49 — explicit log on EVERY outcome (hit / 204 disabled /
    // 404 no snapshot / network fail). Without this an admin can't
    // tell from a debug video whether the device even tried.
    // v1.4.27 — XTREAM-FIRST FAST PATH.
    //
    // When the playlist URL is an Xtream Codes /get.php link and the
    // admin hasn't opted-out via `prefer_m3u_source`, skip the (slow,
    // truncation-prone) M3U parse entirely and build the channels
    // array directly from the JSON API responses. On the user's
    // 400 k-row provider this cuts cold-start from ~120 s to ~10 s
    // AND recovers the ~110 live channels + any Xtream-only VOD /
    // series that the M3U file failed to include.
    //
    // Fallback: if any Xtream API call fails, we drop through to the
    // legacy pre-parse-snapshot / streaming-M3U path so raw-M3U
    // providers keep working.
    let xtreamFirstCreds = null;
    let xtreamFirstCatalog = null;
    const preflightCreds = parseXtreamCreds(playlist.m3u_url);
    const xtreamFirstEnabled =
      !!preflightCreds && playlist?.prefer_m3u_source !== true;
    if (xtreamFirstEnabled) {
      try {
        reportLoadProgress({ phase: "xtream-catalog" });
        logMemory("before-xtream-first");
        const catalogXf = await Promise.race([
          fetchXtreamCatalog(preflightCreds, rawFetchText),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("xtream catalog fetch timed out after 90s")),
              90_000,
            ),
          ),
        ]).catch((e) => {
          debugLog(
            `[loadPlaylist] Xtream-first: catalog fetch failed (${e?.message || e}) — falling back to M3U path`,
          );
          return null;
        });
        if (catalogXf) {
          const totalKnown =
            (catalogXf.liveStreams?.length || 0) +
            (catalogXf.vodStreams?.length || 0) +
            (catalogXf.seriesList?.length || 0);
          if (totalKnown > 0) {
            debugLog(
              `[loadPlaylist] Xtream-first: catalog live=${catalogXf.liveStreams.length} vod=${catalogXf.vodStreams.length} series=${catalogXf.seriesList.length} — building channels from JSON API`,
            );
            try {
              if (typeof window !== "undefined") {
                window.__ZAPP_XTREAM_DIAG__ = {
                  live: catalogXf.liveStreams.length,
                  vod: catalogXf.vodStreams.length,
                  series: catalogXf.seriesList.length,
                  live_categories: catalogXf.categories.live?.size || 0,
                  vod_categories: catalogXf.categories.vod?.size || 0,
                  series_categories: catalogXf.categories.series?.size || 0,
                  server: catalogXf.info?.server_info || null,
                  user: catalogXf.info?.user_info || null,
                  fetched_at: Date.now(),
                  playlist_id: playlist.id,
                  source: "xtream-first",
                };
              }
            } catch { /* noop */ }
            const built = await harvestXtreamExtras(
              [],
              catalogXf,
              preflightCreds,
              { skipSeriesHarvest: !!playlist?.disable_series_preload },
            );
            parsedChannels = built;
            xtreamFirstCreds = preflightCreds;
            xtreamFirstCatalog = catalogXf;
            parseSource = "xtream-api-first";
            logMemory("after-xtream-first");
          } else {
            debugLog(
              `[loadPlaylist] Xtream-first: catalog returned but empty — falling back to M3U path`,
            );
          }
        }
      } catch (e) {
        debugLog(
          `[loadPlaylist] Xtream-first: unexpected error (${e?.message || e}) — falling back to M3U path`,
        );
      }
    }

    try {
      if (parsedChannels) throw new Error("__xf_skip__");
      reportLoadProgress({ phase: "playlist-snapshot" });
      const preParsed = await fetchPlaylistSnapshot(playlist.id);
      if (preParsed === FETCH_METADATA_DISABLED) {
        debugLog(
          `[loadPlaylist] Pre-parse Snapshot DISABLED by admin (HTTP 204) — using legacy streaming fetch + parse`,
        );
      } else if (preParsed && preParsed.body) {
        // Snapshot HIT — Response object. Stream-parse with the same
        // chunked m3u reader used for provider fetch. fetch()
        // decompresses gzip transparently so body is raw M3U text.
        const r = await parseM3uStream(preParsed, reportLoadProgress);
        if (r.channels.length > 0) {
          const createdAt = preParsed.headers?.get?.("X-Snapshot-Created-At") || "<unknown>";
          debugLog(
            `[loadPlaylist] Pre-parse Snapshot HIT: ${r.channels.length.toLocaleString?.() ?? r.channels.length} channels (server pre-parsed ${createdAt})`,
          );
          parsedChannels = r.channels;
          embeddedEpg = r.epgUrl;
          parseSource = "server-snapshot";
        } else {
          debugLog(
            `[loadPlaylist] Pre-parse Snapshot returned 0 channels — falling back to streaming fetch + parse`,
          );
        }
      } else if (preParsed && preParsed.__miss) {
        // v1.3.60 — Specific HTTP failure surface so the user can tell
        // WHY the snapshot didn't load. Previously every 4xx returned
        // `null` and we always logged the misleading "admin hasn't
        // pressed Run now" message.
        const m = preParsed;
        if (m.__miss === SNAPSHOT_NOT_FOUND) {
          debugLog(
            `[loadPlaylist] Pre-parse Snapshot MISS — server returned HTTP ${m.status} for ${m.url}. Either (a) the admin who pressed "Run now" was logged into a DIFFERENT backend than this device talks to, (b) the snapshot file was deleted, or (c) this playlist isn't assigned to your activation key on the server the device queries. Falling back to streaming fetch + parse.`,
          );
        } else if (m.__miss === SNAPSHOT_FORBIDDEN) {
          debugLog(
            `[loadPlaylist] Pre-parse Snapshot MISS — server returned HTTP ${m.status} (auth/assignment). Your activation key isn't assigned this playlist on the backend the device queries, OR the device ID / key headers are stale. Falling back to streaming fetch + parse.`,
          );
        } else if (m.__miss === SNAPSHOT_NETWORK_ERROR) {
          debugLog(
            `[loadPlaylist] Pre-parse Snapshot MISS — network error (${m.error}). Falling back to streaming fetch + parse.`,
          );
        } else {
          debugLog(
            `[loadPlaylist] Pre-parse Snapshot MISS — unknown failure ${JSON.stringify(m)}. Falling back to streaming fetch + parse.`,
          );
        }
      } else {
        debugLog(
          `[loadPlaylist] Pre-parse Snapshot MISS — unexpected null response. Falling back to streaming fetch + parse.`,
        );
      }
    } catch (e) {
      if (e?.message === "__xf_skip__") {
        // Xtream-first fast path already populated parsedChannels — no
        // fallback / pre-parse-snapshot / M3U parse needed.
      } else {
        debugLog(
          `[loadPlaylist] Pre-parse Snapshot FETCH ERROR: ${e?.message || e} — falling back to streaming parse`,
        );
      }
    }

    if (!parsedChannels) {
      if (isElectron() && window.electronAPI?.fetchUrl) {
        debugLog("[loadPlaylist] using Electron IPC fetch");
        const m3uText = await rawFetchText(playlist.m3u_url);
        debugLog(`[loadPlaylist] electron fetch done, size=${m3uText.length}`);
        const r = await parseM3u(m3uText, reportLoadProgress);
        parsedChannels = r.channels;
        embeddedEpg = r.epgUrl;
      } else {
        debugLog("[loadPlaylist] using streaming fetch + parse");
        // v1.3.31 — `cache: "no-store"` was missing here, so the WebView
        // was happily returning an HTTP-cached copy of the M3U on every
        // manual reload, hiding any new episodes the provider had added.
        // v1.4.8 — routed through webFetch so http:// providers work
        // from https:// web-player pages (mixed-content block).
        const resp = await webFetch(playlist.m3u_url, { cache: "no-store" });
        debugLog(`[loadPlaylist] fetch responded status=${resp.status}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        respEtag = resp.headers.get("etag");
        respLastModified = resp.headers.get("last-modified");
        // v1.4.21 — Fall back to whole-text fetch when the streaming
        // reader stalls. Some Smart TV WebViews drop the socket mid-
        // download and leave `reader.read()` hanging forever, matching
        // the "app hangs at 92 % on TV" report. `parseM3uStream` now
        // throws after 45 s of silence — we catch that here and retry
        // with a plain `text()` read which uses a different transport
        // path on the WebView (single response, no chunked streaming).
        try {
          const r = await parseM3uStream(resp, reportLoadProgress);
          parsedChannels = r.channels;
          embeddedEpg = r.epgUrl;
        } catch (streamErr) {
          const msg = streamErr?.message || String(streamErr || "");
          if (/timed out/i.test(msg) || /BodyStreamBuffer/i.test(msg)) {
            debugLog(
              `[loadPlaylist] streaming parse failed (${msg}) — retrying via whole-text fetch fallback`
            );
            logMemory("streaming-failed-refetch");
            // Re-issue the fetch since the previous body was consumed
            // (or cancelled by our timeout).
            const resp2 = await webFetch(playlist.m3u_url, { cache: "no-store" });
            if (!resp2.ok) throw new Error(`HTTP ${resp2.status} on fallback`);
            const text = await resp2.text();
            debugLog(`[loadPlaylist] fallback text fetch done, size=${text.length}`);
            const r = await parseM3u(text, reportLoadProgress);
            parsedChannels = r.channels;
            embeddedEpg = r.epgUrl;
            parseSource = "streaming-fallback-text";
          } else {
            throw streamErr;
          }
        }
      }
    }
    debugLog(`[loadPlaylist] parsed ${parsedChannels.length} channels (source=${parseSource})`);
  logMemory("after-m3u-parse");

    // 3b. Xtream Codes enrichment — when the playlist is /get.php on an Xtream
    // panel we can pull rich VOD / series / live metadata in one round-trip.
    //
    // v1.4.27 — Skip when the Xtream-first fast path already populated
    // channels directly from the JSON API (they already carry every
    // xc_id / poster / tv_archive / plot). Only run the legacy
    // enrichment when the M3U path produced parsedChannels.
    let channels = parsedChannels;
    let xcreds = xtreamFirstCreds;
    let catalog = xtreamFirstCatalog;
    if (xtreamFirstCatalog) {
      debugLog(
        `[loadPlaylist] Xtream-first was used — skipping legacy enrichment pass (channels already carry Xtream metadata)`,
      );
    }
    try {
      if (xtreamFirstCatalog) throw new Error("__xf_skip_enrich__");
      xcreds = parseXtreamCreds(playlist.m3u_url);
      if (xcreds) {
        // v1.4.23 — Safety timeout around the Xtream catalog fetch.
        // On slow Smart TVs, `fetchXtreamCatalog` can take 60-90 s
        // parsing a 50 MB JSON blob synchronously. If it exceeds
        // 90 s we abandon enrichment and continue with the plain
        // M3U catalog rather than let the app hang / OOM.
        logMemory("before-xtream-catalog");
        catalog = await Promise.race([
          fetchXtreamCatalog(xcreds, rawFetchText),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("xtream catalog fetch timed out after 90s")), 90_000),
          ),
        ]).catch((e) => {
          debugLog(`[xtream] catalog fetch failed: ${e?.message || e} — skipping enrichment`);
          return null;
        });
        logMemory("after-xtream-catalog");
        if (catalog) {
          debugLog(
            `[xtream] catalog live=${catalog.liveStreams.length} vod=${catalog.vodStreams.length} series=${catalog.seriesList.length}`,
          );
          // Expose for Settings → Xtream Catalog diagnostic card so
          // power users can compare "how many series did we discover"
          // against competitor apps (vupro etc.) without leaving the app.
          try {
            if (typeof window !== "undefined") {
              window.__ZAPP_XTREAM_DIAG__ = {
                live: catalog.liveStreams.length,
                vod: catalog.vodStreams.length,
                series: catalog.seriesList.length,
                live_categories: catalog.categories.live?.size || 0,
                vod_categories: catalog.categories.vod?.size || 0,
                series_categories: catalog.categories.series?.size || 0,
                server: catalog.info?.server_info || null,
                user: catalog.info?.user_info || null,
                fetched_at: Date.now(),
                playlist_id: playlist.id,
              };
            }
          } catch {/* noop */}
          channels = await enrichChannelsWithXtream(parsedChannels, catalog, xcreds, {
            skipSeriesHarvest: !!playlist?.disable_series_preload,
          });
          logMemory("after-xtream-enrich");
        } else {
          debugLog("[xtream] catalog fetch returned null — series harvest skipped");
          try {
            if (typeof window !== "undefined") {
              window.__ZAPP_XTREAM_DIAG__ = {
                live: 0,
                vod: 0,
                series: 0,
                error: "Xtream catalog fetch returned null (provider unreachable or non-Xtream panel)",
                fetched_at: Date.now(),
                playlist_id: playlist.id,
              };
            }
          } catch {/* noop */}
        }
      } else {
        try {
          if (typeof window !== "undefined") {
            window.__ZAPP_XTREAM_DIAG__ = {
              skipped: true,
              reason: "Playlist URL is not an Xtream Codes panel (no /player_api.php compatible endpoint)",
              fetched_at: Date.now(),
              playlist_id: playlist.id,
            };
          }
        } catch {/* noop */}
      }
    } catch {
      // Enrichment is best-effort; never let it kill the playlist load.
    }

    // 3c. 1-Stream B2C metadata overlay — RESTORED in v1.3.22.
    //
    // The overlay was briefly removed in v1.3.16 because the network
    // fetch was heavy (multi-MB JSON for low logo-fill yield). User
    // confirmed in v1.3.22 that the trade-off was wrong: Plex series
    // details (plot, cast, year, rating) only ever came from this
    // overlay, so removing it left the series drill-down empty.
    //
    // The overlay now also attaches a `b2c_series_info` block to every
    // matched series row so `Browse.jsx` can populate the drill-down
    // header instantly (no on-open round-trip). Plex thumbnails still
    // flow through /api/covers/{hash} via the cover-proxy rewrite in
    // imageCache.js — the overlay ONLY fills metadata, never replaces
    // existing logo_urls.
    try {
      const channelsMissingPoster = channels.filter(
        (c) => !c.logo_url && c.content_type !== "live_tv",
      ).length;
      // v1.3.43 — Smart TV OOM guard.
      // The B2C overlay step downloads a multi-MB JSON and then merges
      // it against EVERY channel row. On 590k-channel catalogs this
      // pushed a low-spec TV WebView past its heap limit and crashed
      // the app (user reported with a debug-overlay video).
      //
      // v1.3.47 — extended the guard to ALWAYS skip on huge catalogs,
      // not just on auto-refresh. The server-snapshot fallback path
      // was still firing the OOM-prone client-side fetch when the
      // admin hadn't yet pressed "Run now" in the Admin panel. Server
      // snapshots remain the recommended path; once the admin runs
      // the harvest devices will see those instantly. Until then the
      // big-catalog devices simply ship without B2C enrichment (cover
      // art still comes from the M3U/Xtream catalog itself).
      const HUGE_CATALOG_THRESHOLD = 200_000;
      const skipB2cForMemory = channels.length > HUGE_CATALOG_THRESHOLD;
      // v1.3.47 — if the admin has explicitly toggled b2c_enabled OFF
      // on this playlist, skip the entire overlay block. The
      // playlists list returned by /api/player/playlists already
      // carries the flag, so this avoids even the snapshot fetch in
      // the common case. (If the client is running on a stale
      // playlists payload we still get the same effect because the
      // server returns 204 → FETCH_METADATA_DISABLED below.)
      if (playlist.b2c_enabled === false) {
        debugLog(
          `[loadPlaylist] Cover & Metadata Harvest disabled by admin — overlay skipped`,
        );
      } else if (playlist.b2c_url && skipB2cForMemory) {
        debugLog(
          `[loadPlaylist] B2C overlay SKIPPED (catalog=${channels.length} > ${HUGE_CATALOG_THRESHOLD} threshold) — ` +
            `run the server-side "Cover & Metadata Harvest" in Admin → Playlists to enrich at the backend`,
        );
      }
      if (playlist.b2c_enabled !== false && playlist.b2c_url && !skipB2cForMemory) {
        // v1.3.44 — Cover & Metadata Harvest server snapshot.
        // Ask the backend for a pre-baked snapshot (compact lookup map
        // built by /admin/playlists/{id}/run-harvest). When present this
        // is ~50× smaller than the raw B2C catalog → no OOM, no auth
        // round-trip, no pagination walk. When the server returns 204
        // (admin disabled b2c) we skip the overlay entirely. When it
        // returns 404 we fall back to the legacy client-side fetch so
        // small / new playlists keep working without an admin step.
        let serverSnapshot = null;
        try {
          reportLoadProgress({ phase: "metadata-snapshot" });
          serverSnapshot = await fetchMetadataSnapshot(playlist.id);
        } catch {
          serverSnapshot = null;
        }

        if (serverSnapshot === FETCH_METADATA_DISABLED) {
          // Server says admin disabled b2c on this playlist (HTTP 204).
          // Skip the overlay entirely — do NOT fall back to client-side
          // B2C. Otherwise we'd OOM the TV on huge catalogs even
          // though the admin explicitly opted out.
          debugLog(
            `[loadPlaylist] Cover & Metadata Harvest disabled by admin (server 204) — overlay skipped`,
          );
        } else if (serverSnapshot && (serverSnapshot.by_name || serverSnapshot.by_series_key)) {
          debugLog(
            `[loadPlaylist] Cover & Metadata Harvest: using server snapshot ` +
              `(${serverSnapshot.item_count || 0} entries, ` +
              `${(serverSnapshot.vod_count || 0)} VOD + ${(serverSnapshot.series_count || 0)} series harvested ${serverSnapshot.created_at || ""})`,
          );
          const norm = (s) => (s || "").toLowerCase().trim();
          const seriesKey = (s) =>
            norm(s)
              .replace(/\s+s\d{1,3}\s*e\d{1,3}.*$/i, "")
              .replace(/\s+\d{1,3}x\d{1,3}.*$/i, "")
              .replace(/[\s\-_:]+$/g, "")
              .trim();
          const byName = serverSnapshot.by_name || {};
          const bySeries = serverSnapshot.by_series_key || {};
          let filledPoster = 0;
          let filledMeta = 0;
          const YIELD_EVERY = 5000;
          for (let i = 0; i < channels.length; i++) {
            const c = channels[i];
            let hit = byName[norm(c.name)];
            if (!hit && c.content_type === "series") {
              hit = bySeries[seriesKey(c.name)];
            }
            if (hit) {
              if (!c.logo_url && hit.logo_url) {
                c.logo_url = hit.logo_url;
                filledPoster += 1;
              }
              if (!c.description && hit.description) c.description = hit.description;
              if (!c.year && hit.year) c.year = hit.year;
              if (!c.rating && hit.rating) c.rating = hit.rating;
              if (c.content_type === "series" && hit.b2c_series_info && !c.b2c_series_info) {
                c.b2c_series_info = hit.b2c_series_info;
                filledMeta += 1;
              }
            }
            if ((i + 1) % YIELD_EVERY === 0) {
              await new Promise((r) => setTimeout(r, 0));
            }
          }
          debugLog(
            `[loadPlaylist] Cover & Metadata Harvest applied — filled ${filledPoster} poster(s), ${filledMeta} series detail block(s)`,
          );
          // Server snapshot satisfied the overlay step — skip the
          // legacy client-side B2C fetch below.
        } else if (serverSnapshot === null) {
          // 204 (admin disabled b2c) — playerApi returns null; legacy
          // 404 also returns null. We can't tell them apart cheaply,
          // so we fall through to the client-side legacy path. When
          // the admin explicitly disabled b2c (server flag) the next
          // refresh after the admin opens a playlist will pick up a
          // playlist.b2c_url=null from the playlists endpoint and
          // shortcut here anyway.
          const b2cOverlayCreds = parseB2cCreds(playlist.b2c_url);
          if (b2cOverlayCreds) {
            reportLoadProgress({ phase: "b2c-overlay" });
          debugLog(
            `[loadPlaylist] Cover & Metadata Harvest: no server snapshot yet — falling back to client-side fetch`,
          );
          const overlayValue = await fetchB2cCatalog(b2cOverlayCreds);
          if (overlayValue && Array.isArray(overlayValue.channels)) {
            const norm = (s) => (s || "").toLowerCase().trim();
            // Strip an `S01E02`-style episode tag and trailing dashes
            // so "Game of Thrones S01E02 Winter…" → "game of thrones".
            const seriesKey = (s) =>
              norm(s)
                .replace(/\s+s\d{1,3}\s*e\d{1,3}.*$/i, "")
                .replace(/\s+\d{1,3}x\d{1,3}.*$/i, "")
                .replace(/[\s\-_:]+$/g, "")
                .trim();
            const byName = new Map();
            const bySeries = new Map();
            for (const r of overlayValue.channels) {
              const k = norm(r.name);
              if (k && !byName.has(k)) byName.set(k, r);
              const sk = seriesKey(r.series_name || r.name);
              if (sk && !bySeries.has(sk)) bySeries.set(sk, r);
            }
            let filledPoster = 0;
            let filledMeta = 0;
            // v1.3.40 — yield to the event loop every 5k channels so
            // the Android TV WebView doesn't trip the ANR watchdog
            // when matching against huge (500k+) catalogs.
            const YIELD_EVERY = 5000;
            for (let i = 0; i < channels.length; i++) {
              const c = channels[i];
              let hit = byName.get(norm(c.name));
              if (!hit && c.content_type === "series") {
                hit = bySeries.get(seriesKey(c.name));
              }
              if (hit) {
                // Fill blanks, NEVER replace.
                if (!c.logo_url && hit.logo_url) {
                  c.logo_url = hit.logo_url;
                  filledPoster += 1;
                }
                if (!c.description && hit.description) {
                  c.description = hit.description;
                }
                if (!c.year && hit.year) c.year = hit.year;
                if (!c.rating && hit.rating) c.rating = hit.rating;
                if (c.content_type === "series" && hit.b2c_series_info && !c.b2c_series_info) {
                  c.b2c_series_info = hit.b2c_series_info;
                  filledMeta += 1;
                }
              }
              if ((i + 1) % YIELD_EVERY === 0) {
                await new Promise((r) => setTimeout(r, 0));
              }
            }
            debugLog(
              `[loadPlaylist] B2C overlay applied — filled ${filledPoster} poster(s), ${filledMeta} series detail block(s)`,
            );
          }
          }
        }
      }
    } catch (e) {
      debugLog(`[loadPlaylist] B2C overlay failed: ${e?.message || e}`);
    }

    // EPG: explicit (admin-provided) takes priority; otherwise use what
    // the M3U declared. Fetched DIRECTLY from the provider — never
    // proxied through our backend.
    //
    // v1.3.71 — EPG fetch is now optional. Skipped on cold-start
    // auto-refresh (~600 KB / ~400 ms savings). LiveTv.jsx triggers
    // an explicit refresh on mount so the EPG arrives just in time
    // for the channel grid.
    const epgUrl = playlist.epg_url || embeddedEpg || null;
    let epgByChannel = {};
    // Detailed diagnostic shape so the Settings → EPG status panel can
    // explain *why* EPG is or isn't showing without making the user
    // dig into devtools.
    let epgDiag = {
      url: epgUrl || null,
      tried: false,
      ok: false,
      error: null,
      bytes: 0,
      programmeCount: 0,
      channelKeysCount: 0,
      sourceChannelMatched: 0,
      sourceChannelTotal: 0,
      sampleUnmatched: [],
      deferred: !loadEpg && !!epgUrl,
      skipped_reason: null,
    };
    // v1.4.20 — Smart TV OOM guard for the LAST unguarded step in the
    // cold-start pipeline: XMLTV parse via DOMParser.
    //
    // Root cause of the reported "AAB crashes on Smart TV during
    // playlist load": DOMParser holds the full XMLTV text (50-100 MB
    // for large IPTV providers) AND a full DOM tree (3-5x text size)
    // in memory simultaneously. Combined with the already-parsed
    // channels array (~30k VOD rows × ~200 bytes each = ~6 MB) this
    // exceeds the ~256 MB Android WebView heap and OOMs the tab.
    //
    // Guard strategy — mirror the B2C `HUGE_CATALOG_THRESHOLD` pattern:
    //   • Skip cold-start EPG when catalog > 40k rows (user's TV
    //     regime with VOD-heavy providers).
    //   • EPG is still lazily fetched by `LiveTv.jsx` via
    //     `ensureEpgLoaded()` when the user opens the Live TV page —
    //     by that point the initial catalog + IDB write are already
    //     off the heap so DOMParser has room to breathe.
    const HUGE_EPG_CATALOG_THRESHOLD = 40_000;
    const skipEpgForMemory =
      (channels || []).length > HUGE_EPG_CATALOG_THRESHOLD;
    if (epgUrl && loadEpg && skipEpgForMemory) {
      const reason =
        `[loadPlaylist] EPG cold-start SKIPPED (channels=${channels.length} > ` +
        `${HUGE_EPG_CATALOG_THRESHOLD} threshold) — will lazy-load via ` +
        `ensureEpgLoaded when Live TV is opened. This prevents Smart TV OOM.`;
      debugLog(reason);
      epgDiag.deferred = true;
      epgDiag.skipped_reason = "huge-catalog-memory-guard";
    } else if (epgUrl && loadEpg) {
      epgDiag.tried = true;
      try {
        logMemory("before-epg-fetch");
        const xml = await rawFetchText(epgUrl);
        epgDiag.bytes = xml ? xml.length : 0;
        logMemory("after-epg-fetch");
        epgByChannel = await parseXmlTv(xml);
        logMemory("after-epg-parse");
        const allKeys = Object.keys(epgByChannel || {});
        epgDiag.channelKeysCount = allKeys.length;
        epgDiag.programmeCount = allKeys.reduce(
          (n, k) => n + ((epgByChannel[k] && epgByChannel[k].length) || 0),
          0,
        );
        // How many of OUR channels can actually find a programme list?
        const matched = new Set();
        const unmatched = [];
        // v1.3.39 — yield every 5k channels so the EPG-match scan
        // doesn't freeze the renderer on huge catalogs.
        const YIELD_EVERY = 5000;
        for (let i = 0; i < (channels || []).length; i++) {
          const c = channels[i];
          if (c.content_type === "live_tv") {
            epgDiag.sourceChannelTotal += 1;
            const key = (c.tvg_id || c.name || "").trim();
            if (key && epgByChannel[key]) {
              matched.add(c.tvg_id || c.name);
            } else if (unmatched.length < 5) {
              unmatched.push(key || "(no tvg-id)");
            }
          }
          if ((i + 1) % YIELD_EVERY === 0) {
            await new Promise((r) => setTimeout(r, 0));
          }
        }
        epgDiag.sourceChannelMatched = matched.size;
        epgDiag.sampleUnmatched = unmatched;
        epgDiag.ok = epgDiag.programmeCount > 0;
      } catch (err) {
        epgDiag.error = (err && (err.message || String(err))) || "unknown";
        try {
          // eslint-disable-next-line no-console
          console.warn("[playlistCache] EPG fetch failed:", err);
        } catch {/* noop */}
        epgByChannel = {};
      }
    }
    // Expose to the rest of the app so Settings / pages can read it.
    try {
      if (typeof window !== "undefined") window.__ZAPP_EPG_DIAG__ = epgDiag;
    } catch {/* noop */}

    const value = {
      channels,
      epgByChannel,
      epgDiag,
      xtream: xcreds
        ? {
            creds: xcreds,
            hasCatalog: !!catalog,
            info: catalog?.info || null,
          }
        : null,
      etag: respEtag,
      last_modified: respLastModified,
      fetched_at: Date.now(),
      source: "network",
    };
    _memCache.set(key, value);
    // Persist async — caller doesn't need to wait.
    //
    // v2: per-record schema. The old single-blob approach blew up
    // structured-clone with `Data cannot be cloned, out of memory` on
    // playlists >50k channels (256 MB TV WebView heap couldn't double
    // the giant array). The new scheme writes meta + N small channel
    // rows + N small EPG rows in 2k-row transactions, peaking at
    // ~10 MB extra during the write. No size cap needed.
    const meta = {
      xtream: value.xtream,
      etag: value.etag,
      last_modified: value.last_modified,
      fetched_at: value.fetched_at,
      channel_count: channels.length,
      // Persist the Xtream catalog counts so the Settings diagnostic
      // card can show them even on a cold cached load (i.e. before the
      // user manually presses "Reload Playlist").
      xtream_diag:
        typeof window !== "undefined" ? window.__ZAPP_XTREAM_DIAG__ || null : null,
    };
    idbPutPlaylist(key, meta, channels, epgByChannel).catch((e) =>
      debugLog(`[loadPlaylist] idbPutPlaylist failed: ${e?.message || e}`)
    );
    debugLog(`[loadPlaylist] DONE channels=${channels.length}`);
    logMemory("DONE");
    return value;
  } catch (err) {
    // Surface the error in the in-app debug console so the user can see
    // exactly why their TV's playlist load broke (network, parse OOM,
    // anything else) instead of staring at a black crash dialog.
    debugLog(`[loadPlaylist] FAILED: ${err?.message || err}`);

    // v1.3.99 — Stale-cache-serve fallback. When the M3U fetch fails
    // (e.g. provider returned HTTP 503 during a peak-hour outage) we
    // used to blank out the whole app: channels array became empty
    // → LiveTvWatch showed "Channel not found" for every click. Now we
    // fall back to the last-known-good playlist that's still in IDB
    // so the user can keep browsing + playing channels the provider
    // still serves. Only kicks in when a prior successful load exists
    // — first-ever cold-start still throws.
    try {
      const staleMeta = await idbGetMeta(key);
      if (staleMeta && staleMeta.fetched_at) {
        const [channels, epgByChannel] = await Promise.all([
          idbGetChannels(key),
          idbGetEpg(key),
        ]);
        if (channels && channels.length > 0) {
          debugLog(
            `[loadPlaylist] provider fetch failed — falling back to stale cache ` +
            `(${channels.length} channels, fetched_at=${new Date(staleMeta.fetched_at).toISOString()})`
          );
          const result = {
            ...staleMeta,
            channels,
            epgByChannel,
            source: "cache-stale",
            stale_reason: err?.message || String(err),
          };
          _memCache.set(key, result);
          return result;
        }
      }
    } catch (fallbackErr) {
      debugLog(`[loadPlaylist] stale-cache fallback also failed: ${fallbackErr?.message || fallbackErr}`);
    }
    throw err;
  } finally {
    endLoad();
  }
}

/** Manual reload — clears caches for one playlist and re-fetches. */
export async function reloadPlaylist(playlist) {
  const key = `pl:${playlist.id}`;
  _memCache.delete(key);
  await idbDeletePlaylist(key);
  // Manual reload should also clear the 24h cover-harvest gate so the
  // backend re-walks the catalog and the user sees fresh Plex art.
  try {
    localStorage.removeItem(`vupp_cover_harvest_at__${playlist.id}`);
  } catch {/* noop */}
  // Manual reload always loads EPG — the user explicitly asked for it.
  return loadPlaylist(playlist, { force: true, loadEpg: true });
}

/**
 * v1.3.71 — on-demand EPG loader called by LiveTv.jsx when the user
 * opens the Live TV page. Skipped EPG on cold start = ~400 ms saved
 * on first paint; this re-loader fills it in just before the user
 * sees a channel grid.
 *
 * Returns `{ epgByChannel, epgDiag, source }`.  Updates the in-memory
 * cache so subsequent calls to loadPlaylist return the EPG too.
 * Best-effort: on failure returns `{ epgByChannel: {}, ... }` so the
 * caller can fall back gracefully.
 */
export async function ensureEpgLoaded(playlist) {
  if (!playlist?.id) {
    return { epgByChannel: {}, epgDiag: null, source: "noop" };
  }
  const key = `pl:${playlist.id}`;
  const cached = _memCache.get(key);
  // If the EPG is already populated (manual reload, previous Live TV
  // open in this session) just hand it back.
  if (
    cached &&
    cached.epgByChannel &&
    Object.keys(cached.epgByChannel).length > 0
  ) {
    return {
      epgByChannel: cached.epgByChannel,
      epgDiag: cached.epgDiag,
      source: "memory",
    };
  }
  const epgUrl = playlist.epg_url || cached?.embeddedEpg || null;
  if (!epgUrl) {
    return { epgByChannel: {}, epgDiag: null, source: "no-url" };
  }
  try {
    const xml = await rawFetchText(epgUrl);
    const epgByChannel = await parseXmlTv(xml);
    const epgDiag = {
      url: epgUrl,
      tried: true,
      ok: Object.keys(epgByChannel || {}).length > 0,
      bytes: xml ? xml.length : 0,
      programmeCount: Object.values(epgByChannel || {}).reduce(
        (n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0,
      ),
      channelKeysCount: Object.keys(epgByChannel || {}).length,
      sourceChannelMatched: 0,
      sourceChannelTotal: 0,
      sampleUnmatched: [],
      lazyLoaded: true,
    };
    try {
      if (typeof window !== "undefined") window.__ZAPP_EPG_DIAG__ = epgDiag;
    } catch {/* noop */}
    if (cached) {
      cached.epgByChannel = epgByChannel;
      cached.epgDiag = epgDiag;
      _memCache.set(key, cached);
    }
    return { epgByChannel, epgDiag, source: "network" };
  } catch (err) {
    debugLog(`[ensureEpgLoaded] failed: ${err?.message || err}`);
    return {
      epgByChannel: {},
      epgDiag: { url: epgUrl, tried: true, ok: false, error: err?.message || String(err) },
      source: "error",
    };
  }
}

/**
 * Cache-only fetch — returns the cached playlist snapshot if present, or
 * `null` if there's nothing in memory / IndexedDB. NEVER touches the
 * network and NEVER kicks the cover-harvest. Used on cold launch to
 * decide whether the "Updating your playlist…" splash needs to flash
 * at all: if a fresh cache is available we skip the loading state and
 * paint the menu instantly. Manual "Reload Playlist" still goes
 * through ``reloadPlaylist`` for a full re-fetch.
 */
export async function loadPlaylistFromCacheOnly(playlist) {
  if (!playlist?.id) return null;
  const key = `pl:${playlist.id}`;
  if (_memCache.has(key)) {
    debugLog(`[loadPlaylist:cache-only] mem hit id=${playlist.id}`);
    return _memCache.get(key);
  }
  await maybeMigrateContentTypes();
  const meta = await idbGetMeta(key);
  if (!meta) {
    debugLog(`[loadPlaylist:cache-only] idb miss (no meta) id=${playlist.id}`);
    return null;
  }
  const [channels, epgByChannel] = await Promise.all([
    idbGetChannels(key),
    idbGetEpg(key),
  ]);
  if (!channels.length) {
    debugLog(`[loadPlaylist:cache-only] idb miss (no channels) id=${playlist.id}`);
    return null;
  }
  try {
    if (typeof window !== "undefined" && meta.xtream_diag) {
      window.__ZAPP_XTREAM_DIAG__ = meta.xtream_diag;
    }
  } catch {/* noop */}
  const result = { ...meta, channels, epgByChannel, source: "cache" };
  _memCache.set(key, result);
  const ageMin = meta.fetched_at
    ? Math.round((Date.now() - meta.fetched_at) / 60000)
    : null;
  debugLog(
    `[loadPlaylist:cache-only] idb hit id=${playlist.id} channels=${channels.length} age=${ageMin}min`,
  );
  return result;
}

/** Reload everything (used by the toolbar "Reload" button). */
export async function reloadAllPlaylists(playlists) {
  // v1.3.76 — flush the in-memory LRU before nuking IDB so the next
  // reads come back fresh from the rebuilt store.
  _lruClearAll();
  for (const pl of playlists) {
    const key = `pl:${pl.id}`;
    _memCache.delete(key);
    await idbDeletePlaylist(key);
  }
}

/**
 * Wipe every cached playlist + EPG (IndexedDB + memory). Used by the
 * "Clear cache" button in Settings.
 */
export async function clearAllPlaylistCaches() {
  _memCache.clear();
  // v1.3.76 — drop everything in the LRU too. Without this the user
  // would see "ghost" data from JS heap until the next cold start.
  _lruClearAll();
  try {
    const db = await openDb();
    // Wipe each of the v2 stores. The legacy v1 `playlists` store was
    // dropped at DB version 2 so we no longer need to touch it.
    for (const name of [META_STORE, CHAN_STORE, EPG_STORE]) {
      await new Promise((resolve) => {
        const tx = db.transaction(name, "readwrite");
        tx.objectStore(name).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    }
  } catch {
    /* no-op */
  }
  // v1.3.82 — also nuke the SQLite mirror so a "Clear cache" is truly
  // a fresh start on native.
  try {
    if (_sqlAvail()) {
      const sql = await import("./playlistCacheSql");
      if (typeof sql.sqlClearAll === "function") {
        await sql.sqlClearAll();
      }
    }
  } catch { /* ignore */ }
}
